/**
 * Simple-profile mint — the OTP-free door (tiered-exchange Plan 1, Step 5).
 *
 * "Get your QR" turns an Anon into a Simple Profile ON THE SAME anonymous
 * auth session: no login, no OTP, no Conversion Grant (held per epic — Simple
 * identity is not OTP-proven). `convert_pending_profile` is untouched; real
 * conversion later compiles ALL the person's anon ids by phone evidence.
 *
 * The mint (order matters):
 *   1. profiles row via the canonical sync shape — owner auth_user_id stamped
 *      (dev RLS requires it), account_status 'anon' (server-written, 038),
 *      source_branch stamped; then the phone's tier copy → 'simple'. (The blob's convertedFromAnonId stays local —
 *      the server stamps the column at step 4; f/identity-ownership.)
 *   2. Local profile keys (profile_user_id, profile_user_data,
 *      user_type='profile') — the conversion local flip minus OTP, so the SP
 *      lands on Profile-world surfaces (D5) and the tier module reads Simple
 *      (profile keys + the stored copy).
 *   3. Local key re-shape (A9 G1): Profile-side surfaces read the peer under
 *      received_anon_data_/profile_notes_; the Anon side wrote
 *      received_profile_data_/anon_notes_. Copy — never move — so nothing is
 *      lost (the mapping mirrors pendingProfile.js's documented table).
 *   4. rekey_simple_profile RPC (A9 G2–G4; v2, migration 037): the server
 *      takes the guest ids this session owns from the register, ledgers them
 *      (first-wins), stamps converted_from_anon_id, and re-keys their DB
 *      footprint. Non-fatal — the localStorage copy already renders.
 */

import { authReady, supabase } from '../supabase';
import {
  getAnonUserId,
  getOrCreateProfileUserId,
  storeProfileUserId,
  recordIdentityAlias,
} from '../../utils/identityUtils';
import {
  getAnonData,
  saveProfileData,
  syncProfileToSupabase,
  syncAllConnectionsToSupabase,
  CONNECTION_METADATA_PREFIX,
  RECEIVED_PROFILE_DATA_PREFIX,
  RECEIVED_ANON_DATA_PREFIX,
  ANON_NOTES_PREFIX,
  PROFILE_NOTES_PREFIX,
} from '../storage/userDataStorage';
import { ALL_CONTACT_KEYS } from '../../utils/contactKeys';
import { persistVariantAtMint } from '../../utils/exchangeVariant';
import { logEvent } from '../analytics/eventLogger';
import { setStoredTier, TIER_SIMPLE } from '../../utils/userTier';
import { recoverGuestSession } from './sessionRecovery';

/**
 * Build the SP's profile blob from the share-back record. Pure — unit-tested.
 * Only fields the person actually shared ride in (sharedFields is the filter,
 * not value-presence — consent rule).
 */
export const buildSimpleProfileBlob = (anonData, { profileUserId, anonUserId }) => {
  if (!anonData?.name) return null;
  const blob = {
    profileUserId,
    name: anonData.name,
    convertedFromAnonId: anonUserId || undefined,
  };
  const sharedFields = anonData.sharedFields || [];
  for (const key of ALL_CONTACT_KEYS) {
    if (sharedFields.includes(key) && anonData[key]) {
      blob[key] = anonData[key];
    }
  }
  return blob;
};

/**
 * Copy the source room's peer data + notes into the Profile-side key shape
 * (A9 G1). Runs over every room the anon holds (connection_metadata_ scan —
 * identity-free, same enumeration the People list uses).
 */
export const rekeyLocalAnonKeysToProfileShape = () => {
  const rooms = Object.keys(localStorage)
    .filter((k) => k.startsWith(CONNECTION_METADATA_PREFIX))
    .map((k) => k.slice(CONNECTION_METADATA_PREFIX.length));

  for (const roomId of rooms) {
    const peerData = localStorage.getItem(`${RECEIVED_PROFILE_DATA_PREFIX}${roomId}`);
    if (peerData && !localStorage.getItem(`${RECEIVED_ANON_DATA_PREFIX}${roomId}`)) {
      localStorage.setItem(`${RECEIVED_ANON_DATA_PREFIX}${roomId}`, peerData);
    }
    const notes = localStorage.getItem(`${ANON_NOTES_PREFIX}${roomId}`);
    if (notes && !localStorage.getItem(`${PROFILE_NOTES_PREFIX}${roomId}`)) {
      localStorage.setItem(`${PROFILE_NOTES_PREFIX}${roomId}`, notes);
    }
  }
  return rooms.length;
};

/**
 * The mint's precondition, as policy (folded from AnonConnectionsListPage,
 * _refactor 2026-08-09 — the second mint entry point is the new guest Done):
 * the seed room is the first candidate whose share-back record holds a name.
 * Candidates are tried in order — callers put the session room first (a
 * just-saved room can hold no record yet).
 *
 * @param {string[]} candidateRooms
 * @returns {Promise<string|null>} the seed room, or null when none holds a name
 */
export const findMintSeedRoom = async (candidateRooms) => {
  for (const room of candidateRooms) {
    const record = await getAnonData(room);
    if (record?.name) return room;
  }
  return null;
};

/**
 * Mint the Simple profile. Throws when no share-back record exists (the CTA
 * is only reachable post-share-back — a person with no name/contact on record
 * can never reach Make-a-QR, flow simplification #3).
 *
 * @param {string} sourceChatRoomId - the room whose share-back seeds the profile
 * @returns {Promise<{profileUserId: string}>}
 */
export const mintSimpleProfile = async (sourceChatRoomId) => {
  await authReady; // dev RLS: the insert must carry auth_user_id = auth.uid()

  const anonUserId = getAnonUserId();
  const anonData = await getAnonData(sourceChatRoomId);
  // Not stored until the server row exists: a stored id with no row makes the
  // server refuse every event from this phone (open-access lockdown)
  const profileUserId = getOrCreateProfileUserId({ persist: false });

  const blob = buildSimpleProfileBlob(anonData, { profileUserId, anonUserId });
  if (!blob) {
    throw new Error('[SIMPLE_MINT] No share-back record — mint requires a name on file');
  }

  // DB row first (canonical sync shape — owner-stamped; the server writes
  // account_status 'anon' on insert, migration 038)
  let synced = await syncProfileToSupabase(blob);
  // Nothing local has changed yet, so one retry is safe — only after the
  // sign-in that vanished from storage is put back (guest-session-loss)
  if (
    synced !== true &&
    (await recoverGuestSession('mint_failed', {
      chatRoomId: sourceChatRoomId,
      error: 'Profile row sync failed',
    }))
  ) {
    synced = await syncProfileToSupabase(blob);
  }
  if (synced !== true) {
    throw new Error('[SIMPLE_MINT] Profile row sync failed');
  }
  storeProfileUserId(profileUserId);
  // The phone's copy of the tier, written at the transition (auth.md § Account Tiers)
  setStoredTier(TIER_SIMPLE);

  // Local mint shape — the conversion flip minus OTP
  await saveProfileData(blob);
  sessionStorage.setItem('user_type', 'profile');

  // Both identities join the alias record: pre-mint history stays attributable
  // whichever identity a later render holds (f/chat-sidedness — the boot path
  // can re-derive user_type='anon' from an Anon entry URL after mint).
  recordIdentityAlias(anonUserId);
  recordIdentityAlias(profileUserId);

  // The device chose a side: persist the mint-time variant so the SP's tier
  // gates survive a bare open (isSimpleTier reads the variant; the scan tag
  // that armed the mint door is per-visit and would be gone) — review
  // 2026-08-13 tier-crack fix.
  persistVariantAtMint();

  // A9 bridge: local key re-shape + DB re-key (non-fatal)
  rekeyLocalAnonKeysToProfileShape();
  try {
    const { data, error } = await supabase.rpc('rekey_simple_profile', {
      p_profile_user_id: profileUserId,
    });
    if (error) throw error;
    console.log('[SIMPLE_MINT] Re-key result:', JSON.stringify(data));
  } catch (err) {
    console.warn('[SIMPLE_MINT] Re-key failed (non-fatal — peer resolves via alias):', err);
    logEvent('simple_mint_rekey_failed', { profile_user_id: profileUserId, message: err.message });
  }

  // Every room's full record onto the server under the new profile. As a
  // guest, the room's details (where/when met) were saved only on this phone —
  // the server write needs a profile id — so without this the server copy
  // holds the contact but no details, and any recovery or merge on the number
  // restores the room invisibly (f/simple-merge B1). Never throws.
  await syncAllConnectionsToSupabase(profileUserId, { includeProfile: false }); // row synced above

  return { profileUserId };
};
